import clsx from 'clsx';
import { Icon } from 'common/icon';

import styles from './styles.module.css';

import { useRef, useState, useEffect } from 'react';
import { useClickOutside } from 'hooks/useClickOutside';
import { useRouter } from 'next/router';

import Link from 'next/link';

const socialIcons = [
  {
    icon: <Icon.TwitterSecond />,
    text: 'Twitter',
    color: '#55ACEE',
    url: 'https://twitter.com/intent/tweet?url=',
  },
  {
    icon: <Icon.FacebookThird />,
    text: 'Facebook',
    color: '#3b5998',
    url: 'https://www.facebook.com/sharer/sharer.php?u=',
  },
  {
    icon: <Icon.LiveJournal />,
    text: 'LiveJournal',
    color: '#09C',
    url: 'https://www.livejournal.com/update.bml?event=',
  },
  {
    icon: <Icon.LinkedIn />,
    text: 'LinkedIn',
    color: '#0077b5',
    url: 'https://www.linkedin.com/shareArticle?mini=true&url=',
  },
  {
    icon: <Icon.Tumblr />,
    text: 'Tumblr',
    color: '#304e6c',
    url: 'https://www.tumblr.com/share/link?url=',
  },
  {
    icon: <Icon.Pinterest />,
    text: 'Pinterest',
    color: '#ee0a23',
    url: 'https://www.pinterest.com/pin/create/button/?url=',
  },
  {
    icon: <Icon.Telegram width="17" height="16" />,
    text: 'Telegram',
    color: '#37AEE2',
    url: 'https://t.me/share/url?url=',
  },
  {
    icon: <Icon.Viber />,
    text: 'Viber',
    color: '#8950a1',
    url: 'viber://forward?text=',
  },
  {
    icon: <Icon.WhatsAppSecond />,
    text: 'WhatsApp',
    color: '#25d366',
    url: 'https://api.whatsapp.com/send?text=',
  },
  {
    icon: <Icon.Reddit />,
    text: 'Reddit',
    color: '#ff4500',
    url: 'https://www.reddit.com/submit?url=',
  },
];

const additionalIcons = [
  { icon: <Icon.Print />, text: 'Print', color: '#498bfa', url: '' },
  {
    icon: <Icon.StarTransparent width="14" height="13" />,
    text: 'Bookmarks',
    color: '#498bfa',
    url: '',
  },
  { icon: <Icon.Email />, text: 'E-mail', color: '#498bfa', url: 'mailto:' },
];

type SocialIconProps = {
  icon: React.JSX.Element;
  text: string;
  color: string;
  url: string;
};

export const SocialIcon = ({ icon, text, color, url }: SocialIconProps) => {
  return (
    <Link className={styles.icon_container} href={url}>
      <div className={styles.backgroundIcon} style={{ backgroundColor: color }}>
        {icon}
      </div>
      <span className={styles.text}>{text}</span>
    </Link>
  );
};

type Props = {
  isVisible: boolean;
  setVisible: (isVisible: boolean) => void;
};

export const ShareModal = ({ isVisible, setVisible }: Props) => {
  const modalRef = useRef<HTMLDivElement | null>(null);

  const handleCloseModal = () => {
    setVisible(false);
  };

  useClickOutside({ onClick: handleCloseModal, ref: modalRef });

  const [currentUrl, setCurrentUrl] = useState<string>('');
  const router = useRouter();
  useEffect(() => {
    if (router.isReady) {
      setCurrentUrl(window.location.href);
    }
  }, [router.isReady]);

  if (!isVisible) {
    return null;
  }

  return (
    <div className={clsx(styles.modalBody, styles.modalBodyActive)}>
      <div className={styles.wrapper} ref={modalRef}>
        <div className={styles.top}>
          <div className={styles.closeButton} onClick={() => setVisible(false)}>
            <Icon.CloseSecond />
          </div>
        </div>
        <div className={styles.icons}>
          <div className={styles.social}>
            <div className={styles.social_icons}>
              {socialIcons.map((item, index) => (
                <SocialIcon
                  key={index}
                  icon={item.icon}
                  text={item.text}
                  color={item.color}
                  url={`${item.url}${encodeURIComponent(currentUrl)}`}
                />
              ))}
            </div>
          </div>
          <div className={clsx(styles.social_icons, styles.additionalIcons)}>
            {additionalIcons.map((item, index) => (
              <SocialIcon
                key={index}
                icon={item.icon}
                text={item.text}
                color={item.color}
                url={item.url}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
